import { CreditCard, OpenNew } from '@/assets/icons'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { Modal, ModalContent, ModalFooterWithActions } from '@/components/modal'
import { SubscriptionActivationResponse, SubscriptionActivationStatus } from '../schemas'

type SubscriptionActivationDialogProps = {
  open: boolean
  data: SubscriptionActivationResponse | null
  busy: boolean
  error: string | null
  onClose: () => void
  onConfirm: () => void
  onRefresh: () => void
}

export function SubscriptionActivationDialog({ open, data, busy, error, onClose, onConfirm, onRefresh }: SubscriptionActivationDialogProps) {
  const ready = data?.status === SubscriptionActivationStatus.Ready
  const active = data?.status === SubscriptionActivationStatus.Active
  const needsMethod = data?.status === SubscriptionActivationStatus.PaymentMethodRequired
  const needsPayment = data?.status === SubscriptionActivationStatus.PaymentRequired
  const quote = data?.quote
  const paymentUrl = data?.payment_url
  const currencyFormatter = quote ? new Intl.NumberFormat('en-US', { style: 'currency', currency: quote.currency }) : null
  const amount =
    quote && currencyFormatter ? currencyFormatter.format(quote.amount_due / 10 ** (currencyFormatter.resolvedOptions().maximumFractionDigits ?? 2)) : null
  const billingInterval = quote?.interval_count === 1 ? quote.interval : `${quote?.interval_count} ${quote?.interval}s`

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose()
      }}
    >
      <ModalContent
        title={active ? 'Subscription activated' : 'Activate subscription'}
        titleIcon={<CreditCard className="size-6 text-brand-neutral-700" aria-hidden />}
        width="md"
        height="max-full"
        showCloseButton
        footer={
          <ModalFooterWithActions
            secondaryAction={
              active
                ? undefined
                : {
                    label: !error && (needsPayment || needsMethod) ? 'Check status' : 'Not now',
                    onClick: !error && (needsPayment || needsMethod) ? onRefresh : onClose,
                    disabled: busy,
                  }
            }
            primaryAction={
              active
                ? { label: 'Done', onClick: onClose }
                : error
                ? { label: 'Check status', onClick: onRefresh, disabled: busy }
                : paymentUrl
                ? {
                    label: needsMethod ? 'Add payment method' : 'Complete payment in Stripe',
                    trailingIcon: <OpenNew className="size-4" aria-hidden />,
                    onClick: () => window.open(paymentUrl, '_blank', 'noopener,noreferrer'),
                    disabled: busy,
                  }
                : ready
                ? { label: busy ? 'Activating…' : 'Start paid subscription', onClick: onConfirm, disabled: busy }
                : { label: 'Refresh', onClick: onRefresh, disabled: busy }
            }
          />
        }
      >
        <div className="space-y-4 text-sm text-brand-neutral-700">
          {error && (
            <InlineMessage variant="neutral">
              <span role="alert">{error}</span>
            </InlineMessage>
          )}
          {busy && <p role="status">{ready ? 'Confirming billing details…' : 'Checking subscription…'}</p>}
          {active ? (
            <p>Your paid subscription is active. Queued OCR documents resume automatically as your usage updates.</p>
          ) : quote ? (
            <>
              <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-2">
                <dt>Current plan</dt>
                <dd className="max-w-64 text-right font-medium break-words">{quote.plan_name}</dd>
                <dt>Billing frequency</dt>
                <dd>Every {billingInterval}</dd>
                <dt>Seats</dt>
                <dd>{quote.quantity.toLocaleString()}</dd>
                <dt className="font-semibold">{needsPayment ? 'Amount due' : 'Due today'}</dt>
                <dd className="font-semibold">{amount}</dd>
              </dl>
              {needsPayment ? (
                <p>
                  Payment is not complete yet. Finish payment or verify your card in Stripe, then return here to check status. Your original trial remains in
                  place while this payment is pending.
                </p>
              ) : needsMethod ? (
                <p>Add a payment method in Stripe, then return here to review and confirm activation. Adding a card does not end your trial.</p>
              ) : (
                <>
                  <p>
                    Starting your paid subscription ends your free trial and begins a new billing period today. Your current plan and seat count stay the same.
                  </p>
                  <p>
                    We will attempt to collect {amount} using your saved payment method. The amount includes applicable taxes, discounts, and account credits.
                  </p>
                  <p>Queued OCR documents resume automatically after activation.</p>
                </>
              )}
            </>
          ) : !busy && !error ? (
            <p>Activation is not available for this account. Contact Support for help with your subscription.</p>
          ) : null}
        </div>
      </ModalContent>
    </Modal>
  )
}
